import { useEffect, useMemo, useState } from "react";

import {
    Button, Typography, TextField, CircularProgress, Link, Select, MenuItem, Collapse,
} from "@material-ui/core";
import { CheckCircle, WhatsApp, Email } from "@material-ui/icons";

import credentialsService from "../../domain/services/credentials.service";

export const CONTACT_CHANNELS = [
    { type: 'whatsapp', label: 'WhatsApp', Icon: WhatsApp, contactKey: 'phone', isPhone: true },
    { type: 'email', label: 'E-mail', Icon: Email, contactKey: 'email', isPhone: false },
];

const COUNTRIES = [
    { code: '+55', label: 'Brasil (+55)' },
    { code: '+1', label: 'EUA/Canadá (+1)' },
    { code: '+351', label: 'Portugal (+351)' },
    { code: '+54', label: 'Argentina (+54)' },
    { code: '+598', label: 'Uruguai (+598)' },
    { code: '+595', label: 'Paraguai (+595)' },
    { code: '+34', label: 'Espanha (+34)' },
    { code: '+39', label: 'Itália (+39)' },
    { code: '+44', label: 'Reino Unido (+44)' },
    { code: '+49', label: 'Alemanha (+49)' },
    { code: '+33', label: 'França (+33)' },
];

const DEFAULT_COUNTRY = '+55';

const VERIFICATION_CODE_FIELD_WIDTH_PX = 144;
const PHONE_FIELD_WIDTH_PX = 224;
const EMAIL_FIELD_WIDTH_PX = 360;
const CHANNEL_PANEL_MIN_WIDTH_PX = 432;
const CHANNEL_PANEL_GAP_PX = 16;
const CHANNEL_PANEL_RADIUS_PX = 8;
const CHANNEL_PANEL_PADDING_PX = 16;

const channelListStyle = { display: 'flex', flexWrap: 'wrap', gap: CHANNEL_PANEL_GAP_PX, alignItems: 'flex-start' };

const CONTACT_TEXT_GAP_PX = 8;

const wrappableTextStyle = { overflowWrap: 'anywhere' };
const wrappableHintStyle = { marginBottom: CONTACT_TEXT_GAP_PX, overflowWrap: 'anywhere' };

const channelPanelStyle = {
    flex: `1 1 ${CHANNEL_PANEL_MIN_WIDTH_PX}px`,
    minWidth: 0,
    border: '1px solid #e0e0e0',
    borderRadius: CHANNEL_PANEL_RADIUS_PX,
    padding: CHANNEL_PANEL_PADDING_PX,
};

const onlyDigits = (value) => (value || '').split('').filter((char) => char >= '0' && char <= '9').join('');

const parsePhone = (e164) => {
    const raw = (e164 || '').trim();
    if (raw.startsWith('+')) {
        const byLength = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);
        const match = byLength.find((country) => raw.startsWith(country.code));
        if (match) return { country: match.code, national: onlyDigits(raw.slice(match.code.length)) };
    }
    return { country: DEFAULT_COUNTRY, national: onlyDigits(raw) };
};

const formatBrNational = (digits) => {
    const value = onlyDigits(digits).slice(0, 11);
    if (value.length <= 2) return value;
    if (value.length <= 6) return `(${value.slice(0, 2)}) ${value.slice(2)}`;
    if (value.length <= 10) return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
    return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`;
};

const PhoneField = ({ value, onChange }) => {
    const { country, national } = useMemo(() => parsePhone(value), [value]);
    const display = country === DEFAULT_COUNTRY ? formatBrNational(national) : national;

    const emit = (nextCountry, nextNational) =>
        onChange(`${nextCountry}${onlyDigits(nextNational)}`);

    return (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <Select
                variant="outlined" margin="dense" value={country}
                onChange={(event) => emit(event.target.value, national)}
                style={{ minWidth: 132 }}
            >
                {COUNTRIES.map((item) => (
                    <MenuItem key={item.code + item.label} value={item.code}>{item.label}</MenuItem>
                ))}
            </Select>
            <TextField
                size="small" variant="outlined" label="Número"
                value={display}
                onChange={(event) => emit(country, onlyDigits(event.target.value))}
                inputProps={{ inputMode: 'numeric' }}
                placeholder={country === DEFAULT_COUNTRY ? '(DD) NNNNN-NNNN' : 'número'}
                style={{ width: PHONE_FIELD_WIDTH_PX, maxWidth: '100%' }}
            />
        </div>
    );
};

export function emptyChannelState() {
    return {
        codeInput: '',
        editing: false,
        newContact: '',
        cooldown: 0,
        sending: false,
        verifying: false,
        sentOnce: false,
        error: '',
    };
}

const ContactChannelsVerifier = ({ setAlert, onAllVerifiedChange, contactFallback, allowEditVerified = false }) => {
    const [status, setStatus] = useState({ whatsapp: { verified: false }, email: { verified: false } });
    const [ui, setUi] = useState({ whatsapp: emptyChannelState(), email: emptyChannelState() });

    const allVerified = CONTACT_CHANNELS.every(({ type }) => (status[type] || {}).verified);

    const updateChannelUi = (type, patch) =>
        setUi((currentUi) => ({ ...currentUi, [type]: { ...currentUi[type], ...patch } }));

    const refreshStatus = async () => {
        const { data } = await credentialsService.getStatus();
        setStatus(data.channels);
        return data;
    };

    useEffect(() => {
        refreshStatus().catch(() => null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (onAllVerifiedChange) onAllVerifiedChange(allVerified);
    }, [allVerified, onAllVerifiedChange]);

    useEffect(() => {
        const hasCooldown = CONTACT_CHANNELS.some(({ type }) => ui[type].cooldown > 0);
        if (!hasCooldown) return;

        const interval = setInterval(() => {
            setUi((currentUi) => {
                const updatedUi = { ...currentUi };
                CONTACT_CHANNELS.forEach(({ type }) => {
                    if (updatedUi[type].cooldown > 0)
                        updatedUi[type] = { ...updatedUi[type], cooldown: updatedUi[type].cooldown - 1 };
                });
                return updatedUi;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [ui]);

    const handleSend = async (type) => {
        const channelUi = ui[type];
        updateChannelUi(type, { sending: true, error: '' });

        try {
            const contactValue = channelUi.editing && channelUi.newContact ? channelUi.newContact : undefined;
            const { data } = await credentialsService.sendCode({ channelType: type, contactValue });

            updateChannelUi(type, {
                sending: false,
                sentOnce: true,
                editing: false,
                cooldown: data.resendAfterSeconds || 60,
            });

            await refreshStatus().catch(() => null);

            if (setAlert)
                setAlert(`Código enviado para o seu ${CONTACT_CHANNELS.find((c) => c.type === type).label}.`, 'success');
        } catch (error) {
            const reason = error?.response?.data?.reason;
            const isCooldown = reason === 'resend_cooldown';
            updateChannelUi(type, {
                sending: false,
                sentOnce: isCooldown ? true : channelUi.sentOnce,
                cooldown: isCooldown ? (error?.response?.data?.resendAfterSeconds || 60) : 0,
                error: isCooldown
                    ? 'Um código já foi enviado. Aguarde para reenviar.'
                    : 'Não foi possível enviar o código. Tente novamente.',
            });
        }
    };

    const handleVerify = async (type) => {
        const channelUi = ui[type];
        if (!channelUi.codeInput || channelUi.codeInput.length < 6) {
            updateChannelUi(type, { error: 'Digite o código de 6 dígitos.' });
            return;
        }

        updateChannelUi(type, { verifying: true, error: '' });

        try {
            const { data } = await credentialsService.verifyCode({ channelType: type, code: channelUi.codeInput });

            if (!data.success) {
                updateChannelUi(type, {
                    verifying: false,
                    error: data.reason === 'expired'
                        ? 'Código expirado. Reenvie um novo.'
                        : data.reason === 'too_many_attempts'
                            ? 'Muitas tentativas. Reenvie um novo código.'
                            : 'Código incorreto.',
                });
                return;
            }

            updateChannelUi(type, { verifying: false, codeInput: '', error: '' });
            await refreshStatus();
        } catch (error) {
            updateChannelUi(type, { verifying: false, error: 'Não foi possível verificar o código.' });
        }
    };

    const renderChannel = ({ type, label, Icon, contactKey, isPhone }) => {
        const channelStatus = status[type] || {};
        const channelUi = ui[type];
        const originalContact = channelStatus.contact || (contactFallback || {})[contactKey] || '';
        const currentContact = channelUi.editing ? channelUi.newContact : originalContact;
        const contactChanged = channelUi.editing && !!channelUi.newContact && channelUi.newContact !== originalContact;
        const cooldownBlocks = channelUi.cooldown > 0 && !contactChanged;
        const sendLabel = channelUi.editing ? 'Enviar código' : (channelUi.sentOnce ? 'Reenviar código' : 'Enviar código');
        const showInteractive = !channelStatus.verified || (allowEditVerified && channelUi.editing);
        const showVerifiedEdit = channelStatus.verified && allowEditVerified && !channelUi.editing;

        return (
            <div key={type} style={channelPanelStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
                    <Icon color={channelStatus.verified ? 'primary' : 'action'} />
                    <Typography variant="h6" style={{ lineHeight: 1 }}>{label}</Typography>
                    {channelStatus.verified && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                            <CheckCircle style={{ color: '#2e7d32' }} fontSize="small" />
                            <Typography style={{ color: '#2e7d32', lineHeight: 1 }}>Verificado</Typography>
                        </div>
                    )}
                </div>

                {showVerifiedEdit && (
                    <div style={{ marginTop: 8 }}>
                        <Typography variant="body2" color="textSecondary" style={wrappableTextStyle}>
                            {originalContact || 'contato não cadastrado'}{' '}
                            <Link
                                component="button" type="button"
                                style={{ verticalAlign: 'baseline', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 'inherit' }}
                                onClick={() => updateChannelUi(type, { editing: true, newContact: originalContact, error: '' })}
                            >
                                alterar
                            </Link>
                        </Typography>
                    </div>
                )}

                {showInteractive && (
                    <div style={{ marginTop: 12 }}>
                        <Collapse in={channelUi.editing} timeout={200} unmountOnExit>
                            {isPhone ? (
                                <PhoneField
                                    value={channelUi.newContact}
                                    onChange={(value) => updateChannelUi(type, { newContact: value })}
                                />
                            ) : (
                                <TextField
                                    size="small" variant="outlined"
                                    label={`Novo ${label}`} value={channelUi.newContact}
                                    onChange={(event) => updateChannelUi(type, { newContact: event.target.value })}
                                    style={{ marginBottom: 8, width: EMAIL_FIELD_WIDTH_PX, maxWidth: '100%' }}
                                />
                            )}
                        </Collapse>

                        <Collapse in={!channelUi.editing} timeout={200} unmountOnExit>
                            <Typography variant="body2" color="textSecondary" style={wrappableHintStyle}>
                                Enviaremos o código para: <strong>{currentContact || 'contato não cadastrado'}</strong>{' '}
                                <Link
                                    component="button" type="button"
                                    style={{ verticalAlign: 'baseline', fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 'inherit' }}
                                    onClick={() => updateChannelUi(type, { editing: true, newContact: currentContact, error: '' })}
                                >
                                    alterar
                                </Link>
                            </Typography>
                        </Collapse>

                        {channelUi.error && (
                            <Typography variant="body2" style={{ color: '#c62828', marginBottom: 8 }}>{channelUi.error}</Typography>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            {channelUi.sentOnce && !channelUi.editing && (
                                <TextField
                                    size="small" variant="outlined"
                                    label="Código"
                                    value={channelUi.codeInput}
                                    onChange={(event) => updateChannelUi(type, { codeInput: onlyDigits(event.target.value).slice(0, 6) })}
                                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                                    style={{ width: VERIFICATION_CODE_FIELD_WIDTH_PX }}
                                />
                            )}

                            {channelUi.sentOnce && !channelUi.editing && (
                                <Button
                                    variant="contained" color="primary" size="small"
                                    disabled={channelUi.verifying || channelUi.codeInput.length < 6}
                                    onClick={() => handleVerify(type)}
                                >
                                    {channelUi.verifying ? <CircularProgress size={18} /> : 'Confirmar'}
                                </Button>
                            )}

                            {cooldownBlocks ? (
                                <Typography variant="body2" color="textSecondary">
                                    Reenviar em {channelUi.cooldown}s
                                </Typography>
                            ) : (
                                <Button
                                    variant={channelUi.sentOnce && !channelUi.editing ? 'text' : 'outlined'}
                                    color="primary" size="small"
                                    disabled={channelUi.sending}
                                    onClick={() => handleSend(type)}
                                >
                                    {channelUi.sending ? <CircularProgress size={18} /> : sendLabel}
                                </Button>
                            )}

                            {channelUi.editing && (
                                <Button size="small" onClick={() => updateChannelUi(type, { editing: false, newContact: '', error: '' })}>
                                    Cancelar
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return <div style={channelListStyle}>{CONTACT_CHANNELS.map(renderChannel)}</div>;
};

export default ContactChannelsVerifier;
